import * as React from 'react';
import { SxProps, Theme } from '@mui/material';
import { IconList } from './IconList';

export type IconColor = 'primary' | 'secondary' | 'inherit';
export type IconSize = 'small' | 'medium' | 'large';
export type IconKey = keyof typeof IconList;

export interface IconProps {
  color?: IconColor;
  className?: string;
  dataProp?: string;
  icon: IconKey;
  fontSize?: IconSize;
  onClick?: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  title?: string;
}

const iconSizes = {
  small: '16px',
  medium: '20px',
  large: '24px'
};

const useStyles = (iconSize: string): Record<string, SxProps<Theme>> => ({
  iconStyle: {
    height: iconSize,
    width: iconSize,
    // ie11 only
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      pointerEvents: 'none'
    }
  }
});

const Icon: React.FunctionComponent<IconProps> = React.forwardRef<SVGSVGElement, IconProps>((props, ref) => {
  const { icon, dataProp = icon, fontSize, className, sx, ...rest } = props;
  const iconSize = iconSizes[fontSize || 'medium'];
  const { iconStyle } = useStyles(iconSize);

  const IconComponent = IconList[icon];

  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production') {
    if (!IconComponent) {
      console.warn(`The icon ${icon} is not valid. If you would like to use this icon, make sure to add it to the @archer/ui-components library.`);
    }
  }
  return IconComponent ? (
    <IconComponent ref={ref} {...rest} sx={[iconStyle, sx] as SxProps<Theme>} className={className} data-selector={dataProp} />
  ) : null;
});

Icon.displayName = 'Icon';

export { Icon };
